import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { accountsSlice } from 'app/store';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAdd, MdFileDownload, MdFilterNone } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import {
    resizableActionCell,
    resizableCheckCell,
    resizableCheckHeaderCell,
    resizableConditionalLabelCell,
    resizableLabelCell,
    resizableLabelHeader,
    resizablePinCell,
    resizableZonePreviewLabelCell,
} from 'components';
import PopupAction from 'components/Misc/PopupActions/components/PopupAction';
import { DuplicateZonesModal } from 'components/Modals/DuplicateZonesModal';
import SearchWithFilters from 'components/SearchWithFilters';
import { tableStyles } from 'components/Tables/TableCells/styles';
import { getAsset } from 'features/assets/assetManagerSlice';
import { clearEditZone, editZone, selectVisualEditZone } from 'features/common/editZoneSlice';
import { closeAllPanel, openPanel, setEditObject } from 'features/common/formSlice';
import { resetTableItems } from 'features/items/itemsTableSlice';
import {
    clearSelectedZone,
    createZoneCategory,
    deleteZoneCategory,
    fetchZoneCategory,
    fetchZonesRange,
    selectAllTableZones,
    selectCategoryStatus,
    selectInitialTableZones,
    selectSelectedZone,
    selectTableZoneStatus,
    selectZoneCategory,
    setSelectedZone,
    updateMultipleZoneCategory,
} from 'features/zones/zonesTableSlice';
import { EnhancedTableToolbar } from 'GeminiViewerComponent/components/EnhancedTableToolbar';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { ResizableTable } from 'GeminiViewerComponent/components/Tables/ResizableTable';
import {
    addZonesToActiveAsset,
    setChildZones,
    setZonesOnActiveAsset,
} from 'GeminiViewerComponent/features/asset/assetSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { debounce, sortBy as lodashSortBy, map } from 'GeminiViewerComponent/helpers/lodashUtils';
import { Action, Role } from 'helpers';
import { useSnackbar } from 'notistack';
import { ZoneItems } from 'scenes/Asset/components/ZoneItems';
import { useStyles } from '../styles';
import ZoneCategoryModal from '../ZoneCategoryModal';
import { VisualEdit } from './VisualEdit';

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

function AssetZones({ asset }) {
    const INIT_ZONES_PAYLOAD = {
        assetId: asset?.asset_id,
        includeItems: true,
        includeChildren: true,
    };
    const dispatch = useDispatch();
    const isComponentLoaded = useRef(true);
    const { enqueueSnackbar } = useSnackbar();
    const initialZones = useSelector(selectInitialTableZones) ?? [];
    const zoneStatus = useSelector(selectTableZoneStatus);
    const allZones = useSelector(selectAllTableZones);
    const selectedZone = useSelector(selectSelectedZone);
    const activeUser = useSelector(accountsSlice.selectActiveUser);
    const zoneCategoryStatus = useSelector(selectCategoryStatus);
    const zoneCategory = useSelector(selectZoneCategory);
    const readOnlyMode = activeUser?.role === Role.User ? true : false;
    // const viewContentPanel = useSelector(selectViewContentPanel);

    const theme = useSelector(selectDefaultTheme);
    const classes = useStyles(theme);
    const tableClasses = tableStyles();
    const [selected, setSelected] = useState([]);
    const [selectedAll, setSelectedAll] = useState(false);
    const [orbitZones, setOrbitZones] = useState([]);
    // const [selectedZone, setSelectedZone] = useState(null);
    const [isLoadingAssetsZones, setLoadingAssetsZones] = useState(false);

    const prevSelectedAll = usePrevious(selectedAll);
    const [zonesPayload] = useState(INIT_ZONES_PAYLOAD);
    const [searchText, setSearchText] = useState('');
    const [sortBy, setSortBy] = useState('display_name');
    const [sortDirection, setSortDirection] = useState('ASC');
    const [openCopyZoneModal, setOpenCopyZoneModal] = useState(false);
    const [openCategoryDialog, setOpenCategoryDialog] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearchText(value ?? '');
        }, 300),
        []
    );

    const zonesForTable = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase();
        const getComparable = (value) => {
            if (value == null) return '';
            if (typeof value === 'number') return value;
            if (typeof value === 'boolean') return value ? 1 : 0;
            return String(value).toLowerCase();
        };

        const filteredZones = normalizedSearch
            ? allZones.filter((zone) => {
                  const searchableFields = [zone?.display_name, zone?.zone_category_name, zone?.asset_name];
                  return searchableFields.some((field) =>
                      String(field ?? '')
                          .toLowerCase()
                          .includes(normalizedSearch)
                  );
              })
            : allZones;

        return [...filteredZones].sort((a, b) => {
            const pinnedDiff = Number(!!b?.is_pinned) - Number(!!a?.is_pinned);
            if (pinnedDiff !== 0) {
                return pinnedDiff;
            }

            const aValue = getComparable(a?.[sortBy]);
            const bValue = getComparable(b?.[sortBy]);

            let valueDiff = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                valueDiff = aValue - bValue;
            } else {
                valueDiff = String(aValue).localeCompare(String(bValue), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            }

            return sortDirection === 'ASC' ? valueDiff : -valueDiff;
        });
    }, [allZones, searchText, sortBy, sortDirection]);

    const removeDeletedZoneCategories = useCallback(
        async (deletedZoneCategories) => {
            if (deletedZoneCategories && deletedZoneCategories?.length > 0) {
                const removeCategoryRequests = [];
                deletedZoneCategories.forEach((category) => {
                    removeCategoryRequests.push(dispatch(deleteZoneCategory(category)));
                });
                const removeCategoryResponses = await Promise.all(removeCategoryRequests);
                let error = false;
                const filteredZoneCategory = removeCategoryResponses.map((res) => {
                    const zoneCat = zoneCategory.find((cat) => cat.zone_category_id === res.meta.arg);
                    if (res.error?.message && zoneCat) {
                        error = true;
                        return zoneCat.display_name + ' - ' + res.error.message;
                    } else {
                        return res.payload?.message;
                    }
                });

                enqueueSnackbar(filteredZoneCategory.join('\n'), {
                    action: (key) => <SnackbarDismiss snackItem={key} />,
                    variant: error ? 'error' : 'success',
                    style: { whiteSpace: 'pre-line' },
                });
                return !error;
            }
            return true;
        },
        [dispatch, enqueueSnackbar, zoneCategory]
    );

    const createZoneCategories = useCallback(
        async (updatedZoneCategories) => {
            const createCategoryRequests = [];
            let zoneCategories = [];
            updatedZoneCategories.forEach((category) => {
                if (typeof category.zone_category_id === 'string') {
                    createCategoryRequests.push(
                        dispatch(
                            createZoneCategory({
                                display_name: category.display_name,
                                asset_id: asset?.asset_id,
                                display_order: category?.display_order,
                                is_active: true
                            })
                        )
                    );
                } else {
                    zoneCategories.push(category);
                }
            });
            let createdZoneCategories = await Promise.all(createCategoryRequests);
            createdZoneCategories.forEach((cat) => {
                if (cat?.error) {
                    dispatch(deactivateLoading());
                }
                zoneCategories.push(cat.payload);
            });
            return zoneCategories.sort(lodashSortBy('display_order'));
        },
        [asset?.asset_id, dispatch]
    );

    const zoneCategoryUpdate = useCallback(
        async (updatedZoneCategories) => {
            const allZoneCategory = updatedZoneCategories.map((cat, idx) => {
                // eslint-disable-next-line no-unused-vars
                const { asset_id, client_id,is_active, ...rest } = cat;
                return { ...rest, display_order: idx + 1, is_active };
            });

            const updatedZoneCategory = await dispatch(updateMultipleZoneCategory(allZoneCategory));
            if (updatedZoneCategories?.error) {
                dispatch(deactivateLoading());
            }
            return updatedZoneCategory.payload;
        },
        [dispatch]
    );

    const updateZoneData = useCallback(
        async (updatedZoneCategories, deletedZoneCategories, zones) => {
            dispatch(activateLoading());
            const zoneCategories = await createZoneCategories(updatedZoneCategories);

            const response = await removeDeletedZoneCategories(deletedZoneCategories);

            await zoneCategoryUpdate(
                zoneCategories?.sort((a, b) =>
                    a.display_order?.toString().localeCompare(b.display_order?.toString(), undefined, { numeric: true })
                )
            );
            if (response) {
                if (zones && zones?.length > 0) {
                    const editZoneRequests = [];
                    zones.forEach((zone) => {
                        editZoneRequests.push(dispatch(editZone(zone)));
                    });
                    await Promise.all(editZoneRequests);
                }
                dispatch(getAsset(asset?.asset_id));
                dispatch(deactivateLoading());
                setOpenCategoryDialog(false);
            }
            dispatch(deactivateLoading());
        },
        [asset?.asset_id, createZoneCategories, dispatch, removeDeletedZoneCategories, zoneCategoryUpdate]
    );

    const handleDownloadAssetDetails = useCallback(() => {
        dispatch(
            setEditObject({
                formKey: 'exportAssetDetailsForm',
                editObject: zonesPayload,
            })
        );
        dispatch(
            openPanel({
                formKey: 'exportAssetDetailsForm',
                formAction: 'Create',
                clearEditId: true,
            })
        );
    }, [dispatch, zonesPayload]);

    const isEditingZone = useSelector(selectVisualEditZone);

    const getZoneData = async (payload, setInitZones = false) => {
        dispatch(activateLoading());
        const response = await dispatch(fetchZonesRange({ ...payload, assetId: asset.asset_id }));
        await dispatch(
            addZonesToActiveAsset({
                assetId: asset?.asset_id,
                zoneInfo: { zones: response.payload.zones },
            })
        );
        if (setInitZones && !isEditingZone && response.payload?.zones && response.payload?.zones?.length > 0) {
            await dispatch(
                setZonesOnActiveAsset({
                    assetId: asset.asset_id,
                    zoneInfo: { zones: response.payload.zones },
                })
            );
        }
        dispatch(deactivateLoading());
    };

    useEffect(() => {
        if (zoneStatus === LoadingStatus.Idle) {
            setLoadingAssetsZones(true);
            getZoneData({ ...zonesPayload }, true);
        }
        (zoneStatus === LoadingStatus.Loaded || zoneStatus === LoadingStatus.Failed) && setLoadingAssetsZones(false);

        if (zoneCategoryStatus === LoadingStatus.Idle && zoneCategoryStatus !== LoadingStatus.Loading) {
            dispatch(
                fetchZoneCategory({
                    asset_id: asset.asset_id,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoneStatus, dispatch, asset.asset_id, zonesPayload, zoneCategoryStatus]);

    // it's called only when assetPayload changes not on component load.
    useEffect(() => {
        if (isComponentLoaded.current) {
            isComponentLoaded.current = false;
            return;
        }
        getZoneData({ ...zonesPayload });
    }, [dispatch, zonesPayload]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (Array.isArray(initialZones)) {
            const index = initialZones.findIndex((x) => x?.zone_id === selectedZone?.zone_id);
            if (index === -1) {
                dispatch(clearSelectedZone());
            }
        }
    }, [initialZones]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const childZones = [];
        if (asset?.children && asset?.children?.length > 0) {
            addChildZonesRecursively(childZones, asset);
        }
        dispatch(setChildZones(childZones));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allZones]);

    useEffect(() => {
        if (asset?.children && asset?.children?.length > 0 && allZones.length > 0) {
            dispatch(
                setZonesOnActiveAsset({
                    assetId: asset.asset_id,
                    zoneInfo: { zones: allZones },
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allZones]);

    if (initialZones === undefined) {
        return null;
    }

    const addChildZonesRecursively = (childZones, asset) => {
        return asset.children.flatMap((childAsset) => {
            childZones.push(
                ...(childAsset?.compact_zones && childAsset?.compact_zones?.length > 0
                    ? childAsset.compact_zones.map((zone) => ({
                          ...zone,
                          asset_id: childAsset.asset_id,
                          asset_name: childAsset.display_name,
                      }))
                    : [])
            );

            addChildZonesRecursively(childZones, childAsset);
        });
    };

    const isSelected = (zone_id) => selected.indexOf(zone_id) !== -1;

    if (prevSelectedAll && selectedAll) {
        if (selected.length !== zonesForTable.length) {
            setSelected(zonesForTable.map((row) => row.zone_id));
            setSelectedAll(true);
        }
    }

    const rowHeight = 60;

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelectedIds = zonesForTable.map((zone) => zone.zone_id);
            setSelected(newSelectedIds);
            setSelectedAll(true);
            const selectedOrbitZones = zonesForTable.filter(
                (zone) => newSelectedIds.includes(zone.zone_id) && zone?.orbit_frame >= 0
            );
            setOrbitZones(map(selectedOrbitZones, 'zone_id'));
            return;
        }
        setSelected([]);
        setSelectedAll(false);
    };

    const handleClick = (event, zone_id) => {
        event.stopPropagation();
        const selectedIndex = selected.indexOf(zone_id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, zone_id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        const selectedOrbitZones = allZones.filter(
            (zone) => newSelected.includes(zone.zone_id) && zone?.orbit_frame >= 0
        );
        setOrbitZones(map(selectedOrbitZones, 'zone_id'));
        setSelected(newSelected);
        setSelectedAll(newSelected.length === zonesForTable.length && zonesForTable.length > 0);
    };

    const handleItemTable = (props) => {
        const { rowData } = props;
        if (selectedZone?.zone_id !== rowData.zone_id) {
            dispatch(setSelectedZone(rowData.zone_id));
        } else {
            dispatch(setSelectedZone(null));
        }
        dispatch(resetTableItems());
    };

    /* start of start sorting */
    const handleSort = (_sortBy, _sortDirection) => {
        setSortBy(_sortBy);
        setSortDirection(_sortDirection);
    };
    /* end of asset sorting */

    let columns = [
        {
            accessorKey: 'zone_id',
            size: 80,
            minSize: 80,
            enableResizing: false,
            padding: 'none',
            label: '',
            actionCellLevel: 'zone',
            targetPopup: 'zone',
            dispatch: dispatch,
            childComponents: [VisualEdit],
            hasHeaderSearchBar: true,
            readOnly: readOnlyMode,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableActionCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'is_pinned',
            size: 50,
            minSize: 50,
            width: 50,
            label: 'Pin',
            enableResizing: false,
            padding: 'checkbox',
            cellLevel: 'zone',
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizablePinCell({ rowData: info?.row?.original }, info?.column?.columnDef),
            hasHeaderSearchBar: true,
        },
        {
            flexGrow: 1,
            size: 250,
            minSize: 250,
            label: 'Name',
            accessorKey: 'display_name',
            padding: 'normal',
            hasHeaderSearchBar: true,
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableZonePreviewLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'zone_category_name',
            padding: 'none',
            label: 'Zone Category',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            hasHeaderSearchBar: true,
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
    ];

    if (readOnlyMode !== true) {
        columns = [
            {
                accessorKey: 'zone_id',
                size: 50,
                minSize: 50,
                enableResizing: false,
                label: 'Select',
                padding: 'checkbox',
                cellLevel: 'zone',
                numSelected: selected.length,
                rowCount: zonesForTable.length,
                handleClick: handleClick,
                handleSelectAllClick: handleSelectAllClick,
                isSelected: isSelected,
                hasHeaderSearchBar: true,
                header: (info) => resizableCheckHeaderCell(null, info?.header?.column?.columnDef),
                cell: (info) => resizableCheckCell({ rowData: info?.row?.original }, info?.column?.columnDef),
            },
            ...columns,
        ];
    }

    if (asset?.children_ids && asset?.children_ids?.length > 0) {
        const assetColumn = {
            accessorKey: 'asset_name',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            label: 'Asset',
            padding: 'none',
            hasHeaderSearchBar: true,
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableConditionalLabelCell(info?.row?.original, info?.column?.columnDef),
        };
        if (readOnlyMode === true) {
            columns.splice(4, 0, assetColumn);
        } else {
            columns.splice(5, 0, assetColumn);
        }
    }

    function loadMoreRows() {}

    const handleCreateZone = () => {
        dispatch(closeAllPanel());
        dispatch(clearEditZone());
        dispatch(
            openPanel({
                formKey: 'zoneForm',
                formAction: 'Create',
                clearEditId: true,
            })
        );
    };

    return (
        <>
            <Box display="flex" justifyContent="space-between">
                <Box
                    sx={{
                        margin: '15px 10px 0',
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                    }}
                >
                    {!readOnlyMode && (
                        <Tooltip title="New Zone">
                            <IconButton className={classes.addNewIcon} onClick={handleCreateZone} size="large">
                                <MdAdd className="react-icon" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!readOnlyMode && (
                        <Button variant="contained" size="small" onClick={() => setOpenCategoryDialog((prev) => !prev)}>
                            Edit Zone Order
                        </Button>
                    )}
                    <Button
                        onClick={handleDownloadAssetDetails}
                        endIcon={<MdFileDownload />}
                        variant="contained"
                        size="small"
                    >
                        Export Asset Details
                    </Button>
                </Box>
                <SearchWithFilters filter={false} onChange={(evt) => debouncedSearch(evt.target.value)} />
            </Box>
            <div
                className={clsx(
                    classes.root,
                    classes.flexGrid,
                    selectedZone != null ? classes.overflowX1080 : classes.overflowX640
                )}
            >
                <div
                    className={clsx(classes.paper, selectedZone != null && classes.assetZones)}
                    style={{
                        height: `calc(100vh - ${selected.length > 0 ? '260px' : '250px'})`,
                        minWidth: '570px',
                        overflow: 'auto hidden',
                    }}
                >
                    {selected.length > 0 && (
                        <EnhancedTableToolbar
                            numSelected={selected.length}
                            totalCount={zonesForTable.length}
                            toolTipTitle={'Delete'}
                        >
                            <Tooltip title={'Duplicate All'} onClick={() => setOpenCopyZoneModal(true)}>
                                <IconButton
                                    onClick={() => setOpenCopyZoneModal(true)}
                                    size="small"
                                    toolTipTitle="Duplicate"
                                >
                                    <MdFilterNone
                                        className="react-icon"
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                            <PopupAction
                                action={Action.DeleteArray}
                                object={{
                                    assetId: asset.asset_id,
                                    zoneIds: selected,
                                    setZoneIds: setSelected,
                                }}
                                level={'zone'}
                                showLabel={false}
                                toolTipTitle="Delete All"
                            />
                        </EnhancedTableToolbar>
                    )}
                    <ResizableTable
                        initialRows={zonesForTable}
                        allRows={zonesForTable}
                        totalRowCount={zonesForTable.length}
                        loadMoreRows={loadMoreRows}
                        disableVirtualization={false}
                        columns={columns}
                        cellClassName={tableClasses.flexContainer}
                        className={tableClasses.table}
                        selected={selected}
                        rowClassName={clsx(tableClasses.searchBarFlexContainer, tableClasses.tableRowHover)}
                        getRowClassName={(row) => (row === selectedZone ? tableClasses.activeRow : null)}
                        rowHeight={rowHeight}
                        onRowClick={handleItemTable}
                        loadingStatus={isLoadingAssetsZones ? LoadingStatus.Loading : 'No zones'}
                        headerStyle={{ backgroundColor: '#eeeeee' }}
                    />
                </div>
                {selectedZone != null && <ZoneItems />}
                {openCopyZoneModal && (
                    <DuplicateZonesModal
                        open={openCopyZoneModal}
                        handleClose={() => setOpenCopyZoneModal(false)}
                        selectedItems={selected}
                        setSelectedItems={setSelected}
                        currentAssetId={asset.asset_id}
                        orbitZones={orbitZones}
                    />
                )}
            </div>
            <ZoneCategoryModal
                asset={asset}
                openDialog={openCategoryDialog}
                setOpenDialog={setOpenCategoryDialog}
                updateZoneData={updateZoneData}
            />
        </>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

export { AssetZones };
